import { hasModule } from "@/lib/access";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * Global source of truth for the caller's ONE active time-tracking session.
 *
 * Contract:
 * - `session` is either null or `{ session_id, task_id, task_name, parent_task_id, parent_task_name, started_at, elapsed_seconds, ... }`
 * - `refresh()` re-fetches from the backend authority
 * - `notifyStarted/Stopped()` broadcasts to sibling tabs and refreshes locally
 * - The backend guarantees at most one running session via a partial-unique index on time_entries.
 */
const ActiveSessionContext = createContext({
  session: null,
  loading: true,
  refresh: async () => {},
  notifyStarted: () => {},
  notifyStopped: () => {},
  stopModalOpen: false,
  openStopModal: () => {},
  closeStopModal: () => {},
  endSegmentModalOpen: false,
  openEndSegmentModal: () => {},
  closeEndSegmentModal: () => {},
});

const CHANNEL_NAME = "kms-obligo-active-session";

export function ActiveSessionProvider({ children }) {
  const { user } = useAuth();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [stopModalOpen, setStopModalOpen] = useState(false);
  const [endSegmentModalOpen, setEndSegmentModalOpen] = useState(false);
  const idlePromptedRef = useRef(false);
  const channelRef = useRef(null);
  const pollRef = useRef(null);

  const fetchNow = useCallback(async () => {
    if (!user || !hasModule(user, "tasks")) { setSession(null); setLoading(false); return; }
    try {
      const { data } = await api.get(`/active-session?tz_offset_min=${-new Date().getTimezoneOffset()}`);
      setSession(data || null);
    } catch (e) {
      // Keep the local view on network hiccups; log for debugging.
      console.warn("Active session refresh failed:", e);
    } finally {
      setLoading(false);
    }
  }, [user]);

  // Initial + cross-tab + polling refresh
  useEffect(() => {
    if (!user || !hasModule(user, "tasks")) { setSession(null); setLoading(false); return; }
    fetchNow();
    // Cross-tab notifications
    try {
      channelRef.current = new BroadcastChannel(CHANNEL_NAME);
      channelRef.current.onmessage = (evt) => {
        if (evt?.data?.type === "refresh") fetchNow();
      };
    } catch { /* browser without BroadcastChannel */ }
    // Heartbeat every 15s from a Web Worker: worker timers aren't throttled when the tab is hidden/minimised.
    let worker = null;
    try {
      const src = "setInterval(() => postMessage('tick'), 15000);";
      worker = new Worker(URL.createObjectURL(new Blob([src], { type: "text/javascript" })));
      worker.onmessage = () => fetchNow();
    } catch { pollRef.current = setInterval(fetchNow, 15000); }
    // Holding a Web Lock keeps Chrome/Edge from freezing this background tab.
    let releaseLock = null;
    try {
      navigator.locks?.request(`kms-timer-${Math.random().toString(36).slice(2)}`, () => new Promise((res) => { releaseLock = res; }));
    } catch (e) { console.warn(e); }
    // Refresh when tab regains focus
    const onFocus = () => fetchNow();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    // Tell the server a tab is going away so a real browser close isn't mistaken for a frozen tab.
    const onPageHide = () => { try { navigator.sendBeacon(`${API}/activity/tab-closed`); } catch (e) { console.warn(e); } };
    window.addEventListener("pagehide", onPageHide);
    return () => {
      window.removeEventListener("pagehide", onPageHide);
      if (pollRef.current) clearInterval(pollRef.current);
      worker?.terminate();
      releaseLock?.();
      channelRef.current?.close();
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [user, fetchNow]);

  const sessionId = session?.session_id;
  const sessionKind = session?.kind;
  const sessionStartedAt = session?.started_at;

  // Idle auto-prompt: after 30 minutes of continuous Idle, gently nudge (once per session).
  useEffect(() => {
    if (sessionKind !== "idle") { idlePromptedRef.current = false; return; }
    const check = () => {
      if (idlePromptedRef.current) return;
      const started = new Date(sessionStartedAt).getTime();
      const elapsedMin = (Date.now() - started) / 60000;
      if (elapsedMin >= 30) {
        idlePromptedRef.current = true;
        toast("Still there? Start a task or step away — your Idle timer keeps running.", {
          duration: 12000,
          id: "idle-30-prompt",
        });
      }
    };
    check();
    const iv = setInterval(check, 60000);
    return () => clearInterval(iv);
  }, [sessionId, sessionKind, sessionStartedAt]);

  // Idle keeps running across refreshes/navigation. The 15s poll above doubles as a heartbeat;
  // the server closes Idle once no tab has checked in for 10 minutes (all tabs closed / laptop asleep).

  const broadcast = () => {
    try { channelRef.current?.postMessage({ type: "refresh", at: Date.now() }); }
    catch (e) { console.warn("Cross-tab broadcast failed:", e); }
  };
  const notifyStarted = useCallback(() => { fetchNow(); broadcast(); }, [fetchNow]);
  const notifyStopped = useCallback(() => { setSession(null); fetchNow(); broadcast(); }, [fetchNow]);
  const openStopModal = useCallback(() => setStopModalOpen(true), []);
  const closeStopModal = useCallback(() => setStopModalOpen(false), []);
  const openEndSegmentModal = useCallback(() => setEndSegmentModalOpen(true), []);
  const closeEndSegmentModal = useCallback(() => setEndSegmentModalOpen(false), []);

  const value = useMemo(() => ({
    session, loading, refresh: fetchNow, notifyStarted, notifyStopped,
    stopModalOpen, openStopModal, closeStopModal,
    endSegmentModalOpen, openEndSegmentModal, closeEndSegmentModal,
  }), [session, loading, fetchNow, notifyStarted, notifyStopped, stopModalOpen, openStopModal,
    closeStopModal, endSegmentModalOpen, openEndSegmentModal, closeEndSegmentModal]);

  return (
    <ActiveSessionContext.Provider value={value}>
      {children}
    </ActiveSessionContext.Provider>
  );
}

export function useActiveSession() {
  return useContext(ActiveSessionContext);
}

/** Format seconds as HH:MM:SS. */
export function fmtElapsed(secs) {
  const s = Math.max(0, Math.floor(secs || 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(r).padStart(2, "0")}`;
}

/** Given an ISO started_at + a re-render ticker, compute live elapsed seconds. */
export function useLiveElapsed(session) {
  const [, setTick] = useState(0);
  const sessionId = session?.session_id;
  useEffect(() => {
    if (!sessionId) return;
    const iv = setInterval(() => setTick((t) => t + 1), 1000);
    return () => clearInterval(iv);
  }, [sessionId]);
  if (!session) return 0;
  const start = new Date(session.started_at).getTime();
  return Math.max(0, Math.floor((Date.now() - start) / 1000));
}
